<app-spinner-overlay
    [showSpinner]="isEditingBooking"
>
</app-spinner-overlay>
<div class="booking-panel bg-body text-body">
    <aside class="booking-panel__sidebar d-none d-md-flex flex-column gap-2 bg-body overflow-y-auto position-relative z-0">
        <ng-container [ngTemplateOutlet]="customerSummary"></ng-container>
        <ng-container
            [ngTemplateOutlet]="bookingSectionsNav"
            [ngTemplateOutletContext]="{
                paymentsTestId: 'payments-tab',
                orderTestId: 'order-tab'
            }"
        >
        </ng-container>
    </aside>

    <main class="booking-panel__main d-flex flex-column bg-body position-relative z-1 border-start border-dark-subtle">
        <header class="d-flex align-items-center justify-content-between gap-3 border-bottom bg-body px-3 py-2">
            <div class="d-flex flex-wrap align-items-center gap-2">
                <h4 class="mb-0 text-body fw-bold">
                    Booking on {{ booking.start | dateTime : venue.timeZone : DateTime.DATE_MED_WITH_WEEKDAY }}
                </h4>
                <div class="hstack gap-2 flex-wrap">
                    @if (viewModel.currentServiceStatus !== null) {
                        <app-service-status-badge
                            [serviceStatus]="viewModel.currentServiceStatus"
                            variant="title-and-icon"
                        >
                        </app-service-status-badge>
                    } @else {
                        <app-booking-status-badge
                            [statusType]="booking.status().type"
                        >
                            {{ booking.status().type | bookingStatusName }}
                        </app-booking-status-badge>
                    }
                    <app-booking-source-badge
                        *ngIf="diaryPreferencesService.showBookingSource$ | async"
                        [source]="booking.source"
                    >
                    </app-booking-source-badge>
                </div>
            </div>
            <div class="hstack gap-2">
                @if (showRebookButton) {
                    <button
                        type="button"
                        class="btn btn-outline-secondary btn-sm py-0"
                        (click)="rebookBooking()"
                    >
                        Rebook
                    </button>
                }
                <button
                    type="button"
                    class="btn-close"
                    data-testid="booking-dismiss-button"
                    (click)="dismissClicked()"
                    ngbAutofocus
                >
                </button>
            </div>
        </header>

        <section class="booking-panel__content flex-grow-1 overflow-auto bg-body">
            <section class="booking-panel__mobile-context d-md-none border-bottom">
                <ng-container [ngTemplateOutlet]="bookingSectionsTabs"></ng-container>
            </section>

            @if (page === 'details') {
                <div class="d-flex flex-column gap-3 p-3">
                    <app-booking-details
                        [business]="business"
                        [venue]="venue"
                        [booking]="booking"
                        [otherBookings]="otherBookings"
                        [schedule]="schedule"
                        (bookingChanged)="bookingChanged.emit($event)"
                        (customerSelected)="customerTabSelected()"
                    >
                    </app-booking-details>

                    @if (hasSecondaryBookingActions) {
                        <div
                            class="booking-panel__secondary-actions list-group list-group-flush border border-light rounded-2 overflow-hidden bg-light"
                            [class.booking-panel__secondary-actions--void-only]="!canResendBookingConfirmation && viewModel.canVoidBooking"
                        >
                            @if (canResendBookingConfirmation) {
                                <button
                                    type="button"
                                    class="list-group-item list-group-item-action d-flex align-items-center gap-3 py-3 fw-semibold"
                                    (click)="resendBookingConfirmationRequested(resendBookingConfirmationModalContent)"
                                >
                                    <img ngSrc="assets/mail/normal.svg" width="20" height="20" alt="">
                                    <span>Resend Confirmation</span>
                                </button>
                            }
                            @if (viewModel.canVoidBooking) {
                                <button
                                    type="button"
                                    class="booking-panel__secondary-void-action list-group-item list-group-item-action align-items-center gap-3 py-3 fw-semibold text-danger"
                                    (click)="voidBooking(confirmVoidModalBody)"
                                >
                                    <img
                                        ngSrc="assets/trash/normal_red.svg"
                                        width="20"
                                        height="20"
                                        alt=""
                                    />
                                    <span>Void Booking</span>
                                </button>
                            }
                        </div>
                    }
                </div>
            }

            @if (page === 'customer') {
                <div class="d-flex flex-column gap-3 p-3">
                    <app-contact-details
                        [business]="business"
                        [venue]="venue"
                        [booking]="booking"
                        (bookingChanged)="bookingChanged.emit($event)"
                    >
                    </app-contact-details>
                </div>
            }

            @if (page === 'previous-visits') {
                <div class="d-flex flex-column h-100 p-3" style="min-height: 0;">
                    @if (booking.customer; as customer) {
                        <app-customer-booking-list
                            [customer]="customer"
                            [excludedBookingId]="booking.id"
                            emptyStateTitle="No Previous Visits"
                            emptyStateDescription="This customer's previous visits will appear here."
                            (bookingSelected)="previousVisitSelected($event)"
                        >
                        </app-customer-booking-list>
                    }
                </div>
            }

            @if (page === 'customer-feedback') {
                @if (booking.customer; as customer) {
                    <app-customer-feedback-list
                        [business]="business"
                        [venue]="venue"
                        [customer]="customer"
                    >
                    </app-customer-feedback-list>
                }
            }

            @if (page === 'messages') {
                <div class="d-flex flex-column h-100">
                    <app-message-thread
                        [venue]="venue"
                        [thread]="messageThread"
                        [booking]="booking"
                        [messages]="messages"
                        [loading]="isLoadingMessages"
                        [sendingMessage]="isSendingMessage"
                        [canSendMessages]="venue.communicationPreferences.messagesEnabled"
                        (messageSent)="sendBookingMessage($event.body, $event.subject)"
                    >
                    </app-message-thread>
                </div>
            }

            @if (page === 'payments') {
                <div class="d-flex flex-column gap-3 p-3">
                    <app-booking-payments
                        [business]="business"
                        [venue]="venue"
                        [booking]="booking"
                        (bookingChanged)="bookingChanged.emit($event)"
                    >
                    </app-booking-payments>
                </div>
            }

            @if (page === 'history') {
                <div class="d-flex flex-column gap-3 p-3">
                    <app-booking-history
                        [organisation]="organisation"
                        [venue]="venue"
                        [booking]="booking"
                    >
                    </app-booking-history>
                </div>
            }

            @if (page === 'order') {
                <div class="d-flex flex-column gap-3 p-3">
                    <app-booking-order
                        [booking]="booking"
                        [venue]="venue"
                        (addOrderClicked)="addOrder()"
                        (editOrderClicked)="editOrder()"
                        (removeOrderClicked)="removeOrder()"
                        (addMenuClicked)="addMenu()"
                        (editDocketClicked)="editDocket($event)"
                        (copyMenuLinkClicked)="copyOrderLink($event)"
                        (copyPaymentLinkClicked)="copyOrderLink()"
                        (resendOrderLinkClicked)="resendOrderLink(resendOrderLinkContent)"
                        (removeDocketClicked)="removeDocket()"
                    />
                </div>
            }
        </section>

        @if (page === 'details' && (hasPrimaryBookingActions || viewModel.canUpdateServiceStatus || viewModel.canVoidBooking)) {
            <footer
                class="booking-panel__compact-footer"
                data-testid="booking-compact-footer"
            >
                <div class="d-flex gap-2 flex-column flex-md-row flex-grow-1">
                    <div class="hstack gap-2 me-md-auto flex-grow-1 flex-md-grow-0 order-1 order-md-0 flex-wrap">
                        @if ((viewModel.showCancelBookingButton || viewModel.showCancelAndChargeButton || viewModel.showCancelAndRefundDepositButton) && viewModel.canVoidBooking) {
                            <div
                                class="btn-group"
                                ngbDropdown
                                container="body"
                            >
                                <ng-container [ngTemplateOutlet]="cancelButtonVariations"></ng-container>
                                <button
                                    type="button"
                                    class="btn btn-outline-danger dropdown-toggle dropdown-toggle-split"
                                    aria-label="More cancellation actions"
                                    ngbDropdownToggle
                                ></button>
                                <div
                                    class="dropdown-menu"
                                    ngbDropdownMenu
                                >
                                    <button
                                        class="dropdown-item fw-semibold"
                                        (click)="voidBooking(confirmVoidModalBody)"
                                        ngbDropdownItem
                                    >
                                        Void Booking
                                    </button>
                                </div>
                            </div>
                        } @else if (viewModel.showCancelBookingButton || viewModel.showCancelAndChargeButton || viewModel.showCancelAndRefundDepositButton) {
                            <ng-container [ngTemplateOutlet]="cancelButtonVariations"></ng-container>
                        } @else if (viewModel.canVoidBooking) {
                            <button
                                class="btn btn-outline-danger me-md-auto flex-grow-1"
                                (click)="voidBooking(confirmVoidModalBody)"
                            >
                                Void Booking
                            </button>
                        }

                        @if (viewModel.showNoShowBookingButton) {
                            <button
                                class="btn btn-outline-secondary"
                                (click)="noShowBookingWithoutCharging(confirmNoShowModalBody)"
                            >
                                Mark as No Show
                            </button>
                        }
                        @if (viewModel.showNoShowAndChargeButton) {
                            <button
                                class="btn btn-outline-secondary me-md-auto flex-grow-1"
                                (click)="noShowBookingWithCharge(confirmNoShowChargeModalBody)"
                            >
                                Mark as No Show
                            </button>
                        }
                        @if (viewModel.showNoShowAndRefundDepositButton) {
                            <button
                                class="btn btn-outline-secondary me-md-auto flex-grow-1"
                                (click)="NoShowBookingAndRefundDeposit(confirmNoShowRefundDepositModalBody)"
                            >
                                Mark as No Show
                            </button>
                        }
                    </div>

                    <div class="hstack gap-2 ms-md-auto order-0 order-md-1 flex-wrap">
                        @if (viewModel.canRejectBooking) {
                            <button
                                class="btn btn-outline-danger me-md-auto flex-grow-1"
                                (click)="rejectBooking(rejectBookingModalBody)"
                            >
                                Reject
                            </button>
                        }
                        @if (viewModel.canRejectAndRefundBooking) {
                            <button
                                class="btn btn-outline-danger me-md-auto flex-grow-1"
                                (click)="rejectAndRefundBooking(rejectAndRefundBookingModalBody)"
                            >
                                Reject
                            </button>
                        }
                        @if (viewModel.canAcceptBooking) {
                            <button
                                class="btn btn-primary me-md-auto flex-grow-1"
                                (click)="acceptBooking()"
                            >
                                Accept
                            </button>
                        }

                        @if (viewModel.canUnseatParty && !viewModel.canSetPartyToWaiting && !viewModel.canUpdateServiceStatus) {
                            <button
                                class="btn btn-outline-info ms-md-auto flex-grow-1"
                                (click)="viewModel.unseatBooking()"
                            >
                                Unseat Party
                            </button>
                        }
                        @if (viewModel.canUnseatParty && (viewModel.canSetPartyToWaiting || viewModel.canUpdateServiceStatus)) {
                            <div
                                class="btn-group ms-md-auto flex-grow-1"
                                ngbDropdown
                                container="body"
                            >
                                <button
                                    type="button"
                                    class="btn btn-outline-info"
                                    (click)="viewModel.unseatBooking()"
                                >
                                    Unseat Party
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-outline-info dropdown-toggle dropdown-toggle-split"
                                    aria-label="More booking actions"
                                    ngbDropdownToggle
                                ></button>
                                <div
                                    class="dropdown-menu"
                                    ngbDropdownMenu
                                >
                                    @if (viewModel.canSetPartyToWaiting) {
                                        <button
                                            class="dropdown-item fw-semibold"
                                            (click)="viewModel.setPartyToWaiting()"
                                            ngbDropdownItem
                                        >
                                            Wait at Bar
                                        </button>
                                    }
                                    @if (viewModel.canUpdateServiceStatus && viewModel.canSetPartyToWaiting) {
                                        <div class="dropdown-divider"></div>
                                    }
                                    @if (viewModel.canUpdateServiceStatus) {
                                        <ng-container [ngTemplateOutlet]="serviceStatusDropdownItems"></ng-container>
                                    }
                                </div>
                            </div>
                        }

                        @if (viewModel.canSeatParty && (viewModel.canPartiallySeatParty || viewModel.showMarkAsRunningLateButton)) {
                            <div
                                class="btn-group ms-md-auto flex-grow-1"
                                ngbDropdown
                                container="body"
                            >
                                <button
                                    type="button"
                                    class="btn btn-info"
                                    data-testid="seat-party-button"
                                    (click)="viewModel.seatBooking()"
                                >
                                    Seat Party
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-info dropdown-toggle dropdown-toggle-split"
                                    aria-label="More seating actions"
                                    ngbDropdownToggle
                                ></button>
                                <div
                                    class="dropdown-menu"
                                    ngbDropdownMenu
                                >
                                    @if (viewModel.showMarkAsRunningLateButton) {
                                        <button
                                            class="dropdown-item fw-semibold"
                                            (click)="viewModel.markAsRunningLate()"
                                            ngbDropdownItem
                                        >
                                            Mark as Running Late
                                        </button>
                                    }
                                    @if (viewModel.canSetPartyToWaiting) {
                                        <button
                                            class="dropdown-item fw-semibold"
                                            (click)="viewModel.setPartyToWaiting()"
                                            ngbDropdownItem
                                        >
                                            Wait at Bar
                                        </button>
                                    }
                                    @if (viewModel.canPartiallySeatParty) {
                                        <button
                                            class="dropdown-item fw-semibold"
                                            (click)="viewModel.partiallySeatBooking()"
                                            ngbDropdownItem
                                        >
                                            Partially Seat Party
                                        </button>
                                    }
                                </div>
                            </div>
                        } @else if (viewModel.canSeatParty) {
                            <button
                                type="button"
                                class="btn btn-info"
                                data-testid="seat-party-button"
                                (click)="viewModel.seatBooking()"
                            >
                                Seat Party
                            </button>
                        }

                        @if (viewModel.canUndoCancellation) {
                            <button
                                class="btn btn-outline-info ms-md-auto flex-grow-1"
                                (click)="viewModel.undoCancellation()"
                            >
                                Undo Cancellation
                            </button>
                        }
                        @if (viewModel.canUpdateServiceStatus && !viewModel.canSeatParty && !viewModel.canUnseatParty && !viewModel.canPartiallySeatParty && !viewModel.showMarkAsRunningLateButton) {
                            <div
                                class="btn-group ms-md-auto flex-grow-1"
                                ngbDropdown
                                container="body"
                            >
                                <button
                                    type="button"
                                    class="btn btn-outline-secondary dropdown-toggle hstack align-items-center"
                                    ngbDropdownToggle
                                >
                                    Service Status
                                </button>
                                <div
                                    class="dropdown-menu"
                                    ngbDropdownMenu
                                >
                                    <ng-container [ngTemplateOutlet]="serviceStatusDropdownItems"></ng-container>
                                </div>
                            </div>
                        }
                        @if (viewModel.canFinishBooking) {
                            <button
                                class="btn btn-success ms-md-auto flex-grow-1"
                                (click)="viewModel.finishBooking()"
                            >
                                Finish Booking
                            </button>
                        }
                        @if (viewModel.canReseatParty) {
                            <button
                                class="btn btn-success ms-md-auto flex-grow-1"
                                (click)="viewModel.reseatBooking()"
                            >
                                Reseat Party
                            </button>
                        }
                    </div>
                </div>
            </footer>
        }
    </main>
</div>

<ng-template #customerSummary>
    <section class="d-flex flex-column align-items-start align-items-md-center gap-1 p-4 pt-md-6 px-lg-5 text-start text-md-center">
        <div class="vstack gap-3">
            @if (booking.customer; as customer) {
                <span
                    class="booking-panel__customer-avatar"
                    data-testid="booking-customer-avatar"
                    aria-hidden="true"
                >
                    {{ customerInitials(customer) }}
                </span>
            }
            <button
                type="button"
                class="btn btn-lg btn-plain booking-panel__summary-button mw-100 h3 fw-bold text-truncate mb-0"
                (click)="customerTabSelected()"
            >
                {{ booking.name || 'Walk-in' }}
            </button>
            <div class="vstack gap-1">
                @if (booking.emailAddress) {
                    <button
                        type="button"
                        class="btn btn-plain fw-normal booking-panel__summary-button mw-100 text-body-secondary text-truncate"
                        (click)="customerTabSelected()"
                    >
                        {{ booking.emailAddress }}
                    </button>
                }
                @if (booking.phoneNumber) {
                    <button
                        type="button"
                        class="btn btn-plain fw-normal booking-panel__summary-button mw-100 text-body-secondary"
                        (click)="customerTabSelected()"
                    >
                        {{ booking.phoneNumber | phoneNumber }}
                    </button>
                }
            </div>
            @if (booking.customer; as customer) {
                <button
                    type="button"
                    class="btn btn-plain booking-panel__summary-button mw-100 text-body-secondary d-inline-flex align-items-center justify-content-center gap-1"
                    appEditableIcon
                    [alwaysShowEditableIcon]="true"
                    (click)="customerTabSelected()"
                >
                    <span class="text-truncate">
                        {{ customer.note() || 'Add a Customer Note' }}
                    </span>
                </button>
            } @else {
                <button
                    type="button"
                    class="btn btn-plain booking-panel__summary-button mw-100 text-body-secondary text-truncate"
                    (click)="customerTabSelected()"
                >
                    Link to Customer Profile
                </button>
            }
        </div>
        <div class="vstack gap-2 align-items-start align-items-md-center mt-2">
            @let visitCount = booking.customer?.numberOfTimesVisited;
            @let noShows = booking.customer?.numberOfNoShowsAndCancellations;
            @let latestReviewScore = booking.customer?.latestReviewScore;
            @if (booking.customer?.vip || booking.customer?.barred) {
                <div class="d-flex flex-wrap justify-content-start justify-content-md-center gap-2">
                    @if (booking.customer?.vip) {
                        <div
                            class="feedback-badge hstack gap-1"
                            appRatingStatus
                            [rating]="5"
                        >
                            VIP
                            <img
                                ngSrc="assets/vip/normal_dark.svg"
                                width="16"
                                height="16"
                                alt=""
                            />
                        </div>
                    }
                    @if (booking.customer?.barred) {
                        <div
                            class="feedback-badge"
                            appRatingStatus
                            [rating]="1"
                        >
                            Barred
                        </div>
                    }
                </div>
            }
            @if (visitCount === 0 || visitCount || noShows === 0 || noShows) {
                <div class="d-flex flex-wrap justify-content-start justify-content-md-center gap-2">
                    @if (visitCount === 0) {
                        <div
                            class="feedback-badge"
                            data-testid="booking-visit-count"
                            appRatingStatus
                            [rating]="5"
                        >
                            1st Visit
                        </div>
                    } @else if (visitCount) {
                        <button
                            type="button"
                            class="feedback-badge border-0"
                            data-testid="booking-visit-count"
                            appRatingStatus
                            [rating]="5"
                            (click)="previousVisitsTabSelected()"
                        >
                            {{ visitCount }}
                            <span class="text-truncate">
                                @if (visitCount === 1) {
                                    Previous Visit
                                } @else {
                                    Previous Visits
                                }
                            </span>
                        </button>
                    }
                    @if (noShows === 0 || noShows) {
                        <div
                            class="feedback-badge"
                            data-testid="booking-cancellation-count"
                            appRatingStatus
                            [rating]="1"
                        >
                            {{ noShows }}
                            <span class="text-truncate">
                                @if (noShows === 1) {
                                    Cancellation
                                } @else {
                                    Cancellations
                                }
                            </span>
                        </div>
                    }
                </div>
            }
            @if (latestReviewScore) {
                <div class="d-flex flex-wrap justify-content-start justify-content-md-center gap-2">
                    <button
                        type="button"
                        class="feedback-badge border-0"
                        appRatingStatus
                        [rating]="latestReviewScore"
                        (click)="customerFeedbackTabSelected()"
                    >
                        {{ latestReviewScore }} / 5
                        <span class="text-truncate">
                            Latest Review
                        </span>
                    </button>
                </div>
            }
        </div>
    </section>
</ng-template>

<ng-template #bookingSectionsTabs>
    <ul class="booking-panel__tabs nav nav-pills px-3 px-md-4 tabbed flex-nowrap overflow-auto" aria-label="Booking sections">
        <li class="nav-item">
            <button
                type="button"
                class="nav-link"
                data-booking-section="details"
                [class.active]="page === 'details'"
                (click)="detailsTabSelected()"
            >
                Details
            </button>
        </li>
        <li class="nav-item">
            <button
                type="button"
                class="nav-link"
                data-booking-section="payments"
                [class.active]="page === 'payments'"
                (click)="paymentsTabSelected()"
            >
                Payments
            </button>
        </li>
        @if (hasOrdersAddon$ | async) {
            <li class="nav-item">
                <button
                    type="button"
                    class="nav-link"
                    data-booking-section="order"
                    [class.active]="page === 'order'"
                    (click)="orderTabSelected()"
                >
                    {{ booking.order?.isPaid ? 'Orders' : 'Pre-orders' }}
                </button>
            </li>
        }
        <li class="nav-item">
            <button
                type="button"
                class="nav-link"
                data-booking-section="history"
                [class.active]="page === 'history'"
                (click)="historyTabSelected()"
            >
                Booking Log
            </button>
        </li>
        <li class="nav-item">
            <button
                type="button"
                class="nav-link"
                data-booking-section="customer"
                [class.active]="page === 'customer'"
                (click)="customerTabSelected()"
            >
                @if (booking.customer) {
                    Customer Profile
                } @else {
                    Customer
                }
            </button>
        </li>
        @if (booking.customer) {
            <li class="nav-item">
                <button
                    type="button"
                    class="nav-link"
                    data-booking-section="previous-visits"
                    [class.active]="page === 'previous-visits'"
                    (click)="previousVisitsTabSelected()"
                >
                    Previous Visits
                </button>
            </li>
            <li class="nav-item">
                <button
                    type="button"
                    class="nav-link"
                    data-booking-section="customer-feedback"
                    [class.active]="page === 'customer-feedback'"
                    (click)="customerFeedbackTabSelected()"
                >
                    Customer Feedback
                </button>
            </li>
        }
        @if (venue.communicationPreferences.messagesEnabled) {
            <li class="nav-item">
                <button
                    type="button"
                    class="nav-link"
                    data-booking-section="messages"
                    [class.active]="page === 'messages'"
                    (click)="messagesTabSelected()"
                >
                    Message Customer
                </button>
            </li>
        }
    </ul>
</ng-template>

<ng-template #bookingSectionsNav let-paymentsTestId="paymentsTestId" let-orderTestId="orderTestId">
    <nav class="booking-panel__menu section-nav nav nav-pills" aria-label="Booking sections">
        <button
            type="button"
            class="nav-link p-3 hstack gap-4"
            data-booking-section="details"
            [class.active]="page === 'details'"
            (click)="detailsTabSelected()"
        >
            <img
                ngSrc="assets/calendar/normal.svg"
                width="20"
                height="20"
                alt=""
            />
            <span>Booking Details</span>
        </button>
        <button
            type="button"
            class="nav-link p-3 hstack gap-4"
            data-booking-section="payments"
            [attr.data-testid]="paymentsTestId"
            [class.active]="page === 'payments'"
            (click)="paymentsTabSelected()"
        >
            <img
                ngSrc="assets/credit-card/normal.svg"
                width="20"
                height="20"
                alt=""
            />
            <span>Payment</span>
            @if (booking.canChargeCancellationFee()) {
                <span class="p-1 bg-warning rounded-circle"></span>
            }
        </button>
        <button
            *ngIf="hasOrdersAddon$ | async"
            type="button"
            class="nav-link p-3 hstack gap-4"
            data-booking-section="order"
            [attr.data-testid]="orderTestId"
            [class.active]="page === 'order'"
            (click)="orderTabSelected()"
        >
            <img
                [ngSrc]="booking.order?.isPaid ? 'assets/tag/normal_dark.svg' : 'assets/food-drink/normal.svg'"
                width="20"
                height="20"
                alt=""
            />
            <span>{{ booking.order?.isPaid ? 'Orders' : 'Pre-orders' }}</span>
        </button>
        <button
            type="button"
            class="nav-link p-3 hstack gap-4"
            data-booking-section="history"
            [class.active]="page === 'history'"
            (click)="historyTabSelected()"
        >
            <img
                ngSrc="assets/calendar_clock/normal.svg"
                width="20"
                height="20"
                alt=""
            />
            <span>Booking Log</span>
        </button>

        <hr class="my-2">

        <button
            type="button"
            class="nav-link p-3 hstack gap-4"
            data-booking-section="customer"
            [class.active]="page === 'customer'"
            (click)="customerTabSelected()"
        >
            <img
                ngSrc="assets/person/normal.svg"
                width="20"
                height="20"
                alt=""
            />
            <span>
                @if (booking.customer) {
                    Customer Profile
                } @else {
                    Customer
                }
            </span>
        </button>
        @if (booking.customer) {
            <button
                type="button"
                class="nav-link p-3 hstack gap-4"
                data-booking-section="previous-visits"
                [class.active]="page === 'previous-visits'"
                (click)="previousVisitsTabSelected()"
            >
                <img
                    ngSrc="assets/calendar_clock/normal.svg"
                    width="20"
                    height="20"
                    alt=""
                />
                <span>Previous Visits</span>
            </button>
            <button
                type="button"
                class="nav-link p-3 hstack gap-4"
                data-booking-section="customer-feedback"
                [class.active]="page === 'customer-feedback'"
                (click)="customerFeedbackTabSelected()"
            >
                <img
                    ngSrc="assets/visible/normal.svg"
                    width="20"
                    height="20"
                    alt=""
                />
                <span>Customer Feedback</span>
            </button>
        }
        @if (venue.communicationPreferences.messagesEnabled) {
            <button
                type="button"
                class="nav-link p-3 hstack gap-4"
                data-booking-section="messages"
                [class.active]="page === 'messages'"
                (click)="messagesTabSelected()"
            >
                <img
                    ngSrc="assets/messages/normal.svg"
                    width="20"
                    height="20"
                    alt=""
                />
                <span>Message Customer</span>
            </button>
        }
    </nav>
</ng-template>

<ng-template #serviceStatusDropdownItems>
    @if (viewModel.currentServiceStatus !== null) {
        <button
            class="dropdown-item fw-semibold"
            (click)="viewModel.updateServiceStatus(null)"
            ngbDropdownItem
        >
            <app-booking-status-badge
                [statusType]="booking.status().type"
            >
                {{ booking.status().type | bookingStatusName }}
            </app-booking-status-badge>
        </button>
        <div class="dropdown-divider"></div>
    }
    @for (serviceStatus of viewModel.enabledServiceStatuses; track serviceStatus) {
        <button
            class="dropdown-item fw-semibold"
            [class.active]="viewModel.currentServiceStatus === serviceStatus"
            (click)="viewModel.updateServiceStatus(serviceStatus)"
            ngbDropdownItem
        >
            <app-service-status-badge
                [serviceStatus]="serviceStatus"
                variant="title-and-icon"
            >
            </app-service-status-badge>
        </button>
    }
</ng-template>

<ng-template #cancelButtonVariations>
    <button
        *ngIf="viewModel.showCancelBookingButton"
        class="btn btn-outline-danger"
        (click)="cancelBookingWithoutCharging(confirmCancelModalBody)"
    >
        Cancel Booking
    </button>
    <button
        *ngIf="viewModel.showCancelAndChargeButton"
        class="btn btn-outline-danger"
        (click)="cancelAndChargeBooking(confirmCancelChargeModalBody)"
    >
        Cancel Booking
    </button>
    <button
        *ngIf="viewModel.showCancelAndRefundDepositButton"
        class="btn btn-outline-danger"
        (click)="cancelAndRefundBookingDeposit(confirmCancelRefundDepositModalBody)"
    >
        Cancel Booking
    </button>
</ng-template>

<ng-template #statusActionModalBody>
    <div class="vstack gap-3">
        <ng-container *ngTemplateOutlet="statusActionTemplate"></ng-container>
        @if (booking.sale; as sale) {
            @if (sale.payment?.datePaid) {
                <section class="border rounded-3 overflow-hidden" data-testid="status-product-refunds" aria-label="Product refunds">
                    <div class="d-flex align-items-center justify-content-between gap-3 p-3">
                        <div class="vstack gap-1">
                            <strong>Product refunds</strong>
                            @if (!sale.payment?.canRefundOnline) {
                                <small class="text-body-secondary">Paid in person. Return selected amounts separately; this action records the refund.</small>
                            }
                            <strong>{{ null | currencyUnit:statusOrderRefundTotal:sale.order.currencyCode }}</strong>
                            <small class="text-body-secondary">{{ selectedStatusOrderLineIds.size }} selected</small>
                        </div>
                        <button type="button" class="btn btn-outline-secondary" [disabled]="statusOrderRefundBusy"
                            [attr.aria-expanded]="showStatusProductRefundItems" [attr.aria-controls]="'status-refund-items-' + booking.id"
                            (click)="showStatusProductRefundItems = !showStatusProductRefundItems">
                            {{ showStatusProductRefundItems ? 'Hide items' : 'Customise' }}
                        </button>
                    </div>
                    @if (showStatusProductRefundItems) {
                        <div class="vstack gap-3 border-top p-3" [id]="'status-refund-items-' + booking.id" data-testid="status-product-refund-items">
                            @for (line of sale.order.lineItems; track line.id) {
                                @if (line.paidOnline) {
                                <label class="d-flex align-items-start gap-3">
                                    @if (sale.isLineRefunded(line.id)) {
                                        <span class="badge text-bg-secondary">Refunded</span>
                                    } @else {
                                        <input type="checkbox" class="form-check-input" [checked]="selectedStatusOrderLineIds.has(line.id)"
                                            [disabled]="statusOrderRefundBusy || !sale.canRefundLine(line.id)" (change)="toggleStatusOrderLine(line.id)" />
                                    }
                                    <span class="flex-grow-1">{{ line.quantity }} × {{ line.name }}
                                        @for (modifier of line.modifiers; track modifier.id) { <small class="d-block text-body-secondary">{{ modifier.name }}</small> }
                                        <small class="d-block text-body-secondary">
                                            @if (sale.isLineRefunded(line.id)) {
                                                Already refunded
                                            } @else if (line.unitAmount() * line.quantity === 0) {
                                                No payment to refund
                                            } @else if (line.customerRefundableUntil) {
                                                @if (line.customerRefundableUntil.getTime() <= statusRefundTime.getTime()) {
                                                    <span class="text-warning-emphasis">Customer refund deadline passed · {{ line.customerRefundableUntil | dateTime:venue.timeZone:DateTime.DATETIME_MED }}</span>
                                                } @else {
                                                    Customer can refund if they cancel before {{ line.customerRefundableUntil | dateTime:venue.timeZone:DateTime.DATETIME_MED }}
                                                }
                                            } @else {
                                                Customer cannot refund
                                            }
                                        </small>
                                    </span>
                                    <strong class="text-nowrap">{{ null | currencyUnit:sale.remainingLineAmount(line.id):sale.order.currencyCode }}</strong>
                                </label>
                                }
                            }
                            <small class="text-body-secondary">You can refund any paid item with money remaining, including items the customer cannot refund.</small>
                        </div>
                    }
                </section>
            } @else {
                <p class="small text-body-secondary mb-0">Products are unpaid. There is nothing to refund for them.</p>
            }
        }
    </div>
</ng-template>

<ng-template #rejectBookingModalBody>
    <span>
        Are you sure you want to reject this booking?
        <br/>
        This action cannot be undone.
    </span>
</ng-template>

<ng-template #rejectAndRefundBookingModalBody>
    <div class="vstack gap-1">
        <span>
            Are you sure you want to reject this booking and refund the deposit of
            <span class="fw-bold">
                {{ null | currencyUnit: booking.deposit?.amount : booking.deposit?.currencyCode }}
            </span>
            to the customer?
        </span>
        <p class="fw-semibold m-0">
            This action cannot be undone.
        </p>
    </div>
</ng-template>

<ng-template #confirmCancelModalBody>
    <ng-container *ngTemplateOutlet="cancellationFailureMessage"></ng-container>
    <div class="vstack gap-2">
        <span>
            Are you sure you want to cancel this booking?
            <br/>
            This action cannot be undone.
        </span>
        <ng-container *ngTemplateOutlet="merchantCancellationReasonField"></ng-container>
    </div>
</ng-template>

<ng-template #confirmNoShowModalBody>
    <ng-container *ngTemplateOutlet="cancellationFailureMessage"></ng-container>
    <span>
        Are you sure you want to mark this booking as a No Show?
        <br/>
        This action cannot be undone.
    </span>
</ng-template>

<ng-template #confirmNoShowChargeModalBody>
    <div class="mb-3">
        <ng-container *ngTemplateOutlet="cancellationFailureMessage"></ng-container>
        <ng-container
            *ngIf="booking.cancellation?.charge(); let charge"
        >
            <p
                *ngIf="charge.lowerBound && !charge.upperBound"
            >
                This booking has a cancellation charge pending because it's party size is
                equal to or greater than
                {{ charge.lowerBound | i18nPlural : partySizePluralMapping }}.
            </p>
            <p
                *ngIf="!charge.lowerBound && charge.upperBound"
            >
                This booking has a cancellation charge pending because it's party size is
                equal to or less than
                {{ charge.upperBound | i18nPlural : partySizePluralMapping }}.
            </p>
            <p
                *ngIf="charge.lowerBound && charge.upperBound"
            >
                This booking has a cancellation charge pending because it's party size is
                between
                {{ charge.lowerBound | i18nPlural : partySizePluralMapping }}
                and
                {{ charge.upperBound | i18nPlural : partySizePluralMapping }}.
            </p>
            <p>
                Would you like to charge the
                <span class="fw-bold">
                    {{ null | currencyUnit: charge.unitAmount : 'GBP' }}
                    <ng-container *ngIf="charge.isPerCover">
                        per person
                    </ng-container>
                </span>
                cancellation fee when cancelling this booking?
            </p>
        </ng-container>
    </div>
</ng-template>

<ng-template #confirmNoShowRefundDepositModalBody>
    <ng-container *ngTemplateOutlet="cancellationFailureMessage"></ng-container>
    <div class="mb-3">
        <p
            *ngIf="viewModel.hasDepositRefundCutOffPassed"
        >
            This booking's deposit refund cut-off time has passed, but you can still refund the deposit if you wish.
            <br/>
            Would you like to refund the deposit when marking this booking as a no-show?
        </p>
        <p
            *ngIf="!viewModel.hasDepositRefundCutOffPassed"
        >
            Would you like to refund the deposit when marking this booking as a no-show?
        </p>
    </div>
</ng-template>

<ng-template #confirmCancelChargeModalBody>
    <div class="mb-3">
        <ng-container *ngTemplateOutlet="cancellationFailureMessage"></ng-container>
        <ng-container
            *ngIf="booking.cancellation?.charge(); let charge"
        >
            <p
                *ngIf="charge.lowerBound && !charge.upperBound"
            >
                This booking has a cancellation charge pending because it's party size is
                equal to or greater than
                {{ charge.lowerBound | i18nPlural : partySizePluralMapping }}.
            </p>
            <p
                *ngIf="!charge.lowerBound && charge.upperBound"
            >
                This booking has a cancellation charge pending because it's party size is
                equal to or less than
                {{ charge.upperBound | i18nPlural : partySizePluralMapping }}.
            </p>
            <p
                *ngIf="charge.lowerBound && charge.upperBound"
            >
                This booking has a cancellation charge pending because it's party size is
                between
                {{ charge.lowerBound | i18nPlural : partySizePluralMapping }}
                and
                {{ charge.upperBound | i18nPlural : partySizePluralMapping }}.
            </p>
            <p>
                Would you like to charge the
                <span class="fw-bold">
                    {{ null | currencyUnit: charge.unitAmount : 'GBP' }}
                    <ng-container *ngIf="charge.isPerCover">
                        per person
                    </ng-container>
                </span>
                cancellation fee when cancelling this booking?
            </p>
        </ng-container>
        <ng-container *ngTemplateOutlet="merchantCancellationReasonField"></ng-container>
    </div>
</ng-template>

<ng-template #confirmCancelRefundDepositModalBody>
    <ng-container *ngTemplateOutlet="cancellationFailureMessage"></ng-container>
    <div class="mb-3">
        <p
            *ngIf="viewModel.hasDepositRefundCutOffPassed"
        >
            This booking's deposit refund cut-off time has passed, but you can still refund the deposit if you wish.
            <br/>
            Would you like to refund the deposit when cancelling this booking?
        </p>
        <p
            *ngIf="!viewModel.hasDepositRefundCutOffPassed"
        >
            Would you like to refund the deposit when cancelling this booking?
        </p>
        <ng-container *ngTemplateOutlet="merchantCancellationReasonField"></ng-container>
    </div>
</ng-template>

<ng-template #merchantCancellationReasonField>
    @if (shouldAskMerchantCancellationReason) {
        <div class="vstack gap-1 mt-2">
            <label
                for="merchantCancellationReason"
                class="form-label fw-semibold mb-0"
            >
                Cancellation Reason
                @if (!merchantCancellationReasonRequired) {
                    <span class="text-body-secondary fw-normal">
                        (optional)
                    </span>
                }
            </label>
            <select
                id="merchantCancellationReason"
                class="form-select"
                data-testid="merchant-cancellation-reason"
                [formControl]="merchantCancellationReasonId"
                [required]="merchantCancellationReasonRequired"
                [class.is-invalid]="showMerchantCancellationReasonValidation"
            >
                <option [ngValue]="null">
                    {{ merchantCancellationReasonRequired ? 'Select a reason' : 'No reason' }}
                </option>
                @for (reason of merchantCancellationReasons; track reason.id) {
                    <option [ngValue]="reason.id">
                        {{ reason.displayName }}
                    </option>
                }
            </select>
            @if (showMerchantCancellationReasonValidation) {
                <div class="invalid-feedback d-block">
                    Choose a cancellation reason.
                </div>
            }
        </div>
    }
</ng-template>

<ng-template #confirmVoidModalBody>
    <ng-container *ngTemplateOutlet="cancellationFailureMessage"></ng-container>
    <span>
        Are you sure you want to void this booking?
        <br/>
        <span class="fw-semibold">
            Voiding a booking will remove it from the diary and not count towards any reports.
        </span>
        <br/>
        This action cannot be undone.
    </span>
</ng-template>

<ng-template #resendBookingConfirmationModalContent>
    <div class="vstack gap-2">
        <span>
            How would you like to resend the booking confirmation?
        </span>
        <div class="vstack gap-2 align-content-stretch">
            <label
                class="btn btn-outline-secondary p-2 d-flex flex-column justify-content-center align-items-center"
                [class.disabled]="!booking.emailAddress"
            >
                <div class="m-1 hstack gap-2">
                    <input
                        type="radio"
                        [value]="'email'"
                        [checked]="bookingConfirmationDeliveryMethod.value === 'email'"
                        (change)="bookingConfirmationDeliveryMethod.setValue('email')"
                        class="form-check-input m-0"
                        role="button"
                    >
                    <div class="vstack align-items-start">
                        <span
                            *ngIf="booking.emailAddress"
                            class="fw-semibold"
                        >
                            Email: {{ booking.emailAddress }}
                        </span>
                        <span
                            *ngIf="!booking.emailAddress"
                            class="fw-normal"
                        >
                            Email: Email address not provided
                        </span>
                    </div>
                </div>
            </label>
            <label
                class="btn btn-outline-secondary p-2 d-flex flex-column justify-content-center align-items-center"
                [class.disabled]="!booking.phoneNumber"
            >
                <div class="m-1 hstack gap-2">
                    <input
                        type="radio"
                        [value]="'sms'"
                        [checked]="bookingConfirmationDeliveryMethod.value === 'sms'"
                        (change)="bookingConfirmationDeliveryMethod.setValue('sms')"
                        class="form-check-input m-0"
                        role="button"
                    >
                    <div class="vstack align-items-start">
                        <span
                            *ngIf="booking.phoneNumber"
                            class="fw-semibold"
                        >
                            SMS: {{ booking.phoneNumber | phoneNumber }}
                        </span>
                        <span
                            *ngIf="!booking.phoneNumber"
                            class="fw-normal"
                        >
                            SMS: Phone number not provided
                        </span>
                    </div>
                </div>
            </label>
        </div>
    </div>
</ng-template>

<ng-template #resendOrderLinkContent>
    <div class="vstack gap-2">
        <p class="mb-0">{{ booking.canPayForOrder() ? 'Send the product payment link' : 'Send the pre-order menu link' }} to the booking contact. You can send it again whenever needed.</p>
        <div class="vstack gap-2 align-content-stretch">
            <label
                class="btn btn-outline-secondary p-2 d-flex flex-column justify-content-center align-items-center"
                [class.disabled]="!booking.emailAddress"
            >
                <div class="m-1 hstack gap-2">
                    <input
                        type="radio"
                        [value]="'email'"
                        [checked]="orderLinkDeliveryMethod.value === 'email'"
                        [disabled]="!booking.emailAddress"
                        (change)="orderLinkDeliveryMethod.setValue('email')"
                        class="form-check-input m-0"
                        role="button"
                    >
                    <div class="vstack align-items-start">
                        <span
                            *ngIf="booking.emailAddress"
                            class="fw-semibold"
                        >
                            Email: {{ booking.emailAddress }}
                        </span>
                        <span
                            *ngIf="!booking.emailAddress"
                            class="fw-normal"
                        >
                            Email: Email address not provided
                        </span>
                    </div>
                </div>
            </label>
            <label
                class="btn btn-outline-secondary p-2 d-flex flex-column justify-content-center align-items-center"
                [class.disabled]="!booking.phoneNumber"
            >
                <div class="m-1 hstack gap-2">
                    <input
                        type="radio"
                        [value]="'sms'"
                        [checked]="orderLinkDeliveryMethod.value === 'sms'"
                        [disabled]="!booking.phoneNumber"
                        (change)="orderLinkDeliveryMethod.setValue('sms')"
                        class="form-check-input m-0"
                        role="button"
                    >
                    <div class="vstack align-items-start">
                        <span
                            *ngIf="booking.phoneNumber"
                            class="fw-semibold"
                        >
                            SMS: {{ booking.phoneNumber | phoneNumber }}
                        </span>
                        <span
                            *ngIf="!booking.phoneNumber"
                            class="fw-normal"
                        >
                            SMS: Phone number not provided
                        </span>
                    </div>
                </div>
            </label>
        </div>
    </div>
</ng-template>

<ng-template #cancellationFailureMessage>
        <div
            *ngIf="chargeFailureMessage"
            class="alert alert-danger py-2 px-3 mb-2"
            role="alert"
        >
            {{ chargeFailureMessage }}
        </div>
</ng-template>
